/**
 * Report Service
 * Handles report generation and analytics
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS } from '../config.js'
import { http } from '@/utils/httpClient'

const ROW_FILTER_KEYS = [
  'page', 'limit', 'search',
  'teamIds', 'projectIds', 'featureIds',
  'status', 'priority', 'severity', 'type', 'caseType', 'testType',
  'automationStatus', 'environment',
  'devAssignees', 'qaAssignees', 'assignees', 'createdBy',
  'includeDeleted', 'includeSubTasks',
  'startDateFrom', 'startDateTo', 'dueDateFrom', 'dueDateTo',
  'createdFrom', 'createdTo',
  'estimatedTimeMin', 'estimatedTimeMax',
  'rootFolderId',
]

function buildRowQuery(filters = {}) {
  const params = new URLSearchParams()
  for (const key of ROW_FILTER_KEYS) {
    const v = filters[key]
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length === 0) continue
      params.set(key, v.join(','))
    } else if (v instanceof Date) {
      params.set(key, v.toISOString())
    } else {
      params.set(key, String(v))
    }
  }
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export class ReportService extends BaseService {
  constructor() {
    super(COLLECTIONS.ACTIVITIES) // Using activities as base collection
  }

  /**
   * Slim overview for /reports — teams/projects/users with denormalized
   * counts plus KPI totals. Mirrors /projects/dashboard-stats.
   */
  async getOverview() {
    return await http.get('/reports/overview')
  }

  async getFeatures(filters = {}) {
    return await http.get(`/reports/features${buildRowQuery(filters)}`)
  }

  async getTestCases(filters = {}) {
    return await http.get(`/reports/test-cases${buildRowQuery(filters)}`)
  }

  async getTestRuns(filters = {}) {
    return await http.get(`/reports/test-runs${buildRowQuery(filters)}`)
  }

  async getBugs(filters = {}) {
    return await http.get(`/reports/bugs${buildRowQuery(filters)}`)
  }

  /**
   * Aggregated chart counts for the project Reports — Features tab.
   * Returns pre-bucketed data for status, priority, timeline, devs, QAs,
   * and top-by-activity in a single round-trip.
   */
  async getFeaturesMetrics(filters = {}) {
    return await http.get(`/reports/features/metrics${buildRowQuery(filters)}`)
  }

  /**
   * Aggregated chart counts for the project Reports — Test Cases tab.
   * Returns pre-bucketed data for status, priority, automation, type,
   * case-type, estimation buckets, and the created-over-time timeline.
   */
  async getTestCasesMetrics(filters = {}) {
    return await http.get(`/reports/test-cases/metrics${buildRowQuery(filters)}`)
  }

  /** Per-folder automation coverage for project Reports — Test Cases tab. */
  async getFolderCoverage(filters = {}) {
    return await http.get(`/reports/folder-coverage${buildRowQuery(filters)}`)
  }

  /**
   * Aggregated chart counts for the project Reports — Test Runs tab.
   * Returns pre-bucketed data for status, environment, creator, the
   * created-over-time timeline, plus a precomputed completionRate (0..1)
   * for the COMPLETED-vs-total tile.
   */
  async getTestRunsMetrics(filters = {}) {
    return await http.get(`/reports/test-runs/metrics${buildRowQuery(filters)}`)
  }

  /**
   * Aggregated chart counts for the project Reports — Bugs tab.
   * Returns pre-bucketed data for status, severity, priority, source
   * (test case vs test run), assignee, and the created-over-time timeline.
   */
  async getBugsMetrics(filters = {}) {
    return await http.get(`/reports/bugs/metrics${buildRowQuery(filters)}`)
  }

  /**
   * Generate dashboard summary
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Dashboard summary
   */
  async getDashboardSummary(userId) {
    // This would typically aggregate data from multiple services
    // For now, return a basic structure
    return {
      userId,
      summary: {
        totalProjects: 0,
        totalTestCases: 0,
        totalTestRuns: 0,
        totalBugs: 0,
        openBugs: 0,
        recentActivity: []
      },
      trends: {
        testExecution: [],
        bugTrends: [],
        activityTrends: []
      },
      lastUpdated: new Date()
    }
  }

  /**
   * Generate project report
   * @param {string} projectId - Project ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Project report
   */
  async getProjectReport(projectId, options = {}) {
    return {
      projectId,
      report: {
        overview: {},
        testCases: {},
        testRuns: {},
        bugs: {},
        activities: {}
      },
      generatedAt: new Date()
    }
  }

  /**
   * Generate team report
   * @param {string} teamId - Team ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Team report
   */
  async getTeamReport(teamId, options = {}) {
    return {
      teamId,
      report: {
        overview: {},
        projects: {},
        members: {},
        activities: {}
      },
      generatedAt: new Date()
    }
  }

  /**
   * Generate test execution report
   * @param {string} projectId - Project ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Test execution report
   */
  async getTestExecutionReport(projectId, options = {}) {
    return {
      projectId,
      report: {
        summary: {},
        trends: {},
        details: []
      },
      generatedAt: new Date()
    }
  }

  /**
   * Generate bug report
   * @param {string} projectId - Project ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Bug report
   */
  async getBugReport(projectId, options = {}) {
    return {
      projectId,
      report: {
        summary: {},
        trends: {},
        details: []
      },
      generatedAt: new Date()
    }
  }

  /**
   * Generate activity report
   * @param {string} projectId - Project ID
   * @param {Object} options - Report options
   * @returns {Promise<Object>} Activity report
   */
  async getActivityReport(projectId, options = {}) {
    return {
      projectId,
      report: {
        summary: {},
        trends: {},
        details: []
      },
      generatedAt: new Date()
    }
  }

  /**
   * Export data to CSV
   * @param {string} collectionName - Collection name
   * @param {Array} data - Data to export
   * @param {Object} options - Export options
   * @returns {Promise<string>} CSV data
   */
  async exportToCSV(collectionName, data, options = {}) {
    if (!data || data.length === 0) {
      return ''
    }

    const headers = Object.keys(data[0])
    const csvRows = []

    // Add headers
    csvRows.push(headers.map(header => `"${header}"`).join(','))

    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header]
        return `"${value === null || value === undefined ? '' : String(value).replace(/"/g, '""')}"`
      })
      csvRows.push(values.join(','))
    }

    return csvRows.join('\n')
  }

  /**
   * Export data to JSON
   * @param {Array} data - Data to export
   * @param {Object} options - Export options
   * @returns {Promise<string>} JSON data
   */
  async exportToJSON(data, options = {}) {
    return JSON.stringify(data, null, 2)
  }

  /**
   * Get report templates
   * @returns {Array} Available report templates
   */
  getReportTemplates() {
    return [
      {
        id: 'dashboard',
        name: 'Dashboard Summary',
        description: 'Overview of all key metrics',
        type: 'summary'
      },
      {
        id: 'project',
        name: 'Project Report',
        description: 'Detailed project analysis',
        type: 'detailed'
      },
      {
        id: 'team',
        name: 'Team Report',
        description: 'Team performance and activity',
        type: 'detailed'
      },
      {
        id: 'test_execution',
        name: 'Test Execution Report',
        description: 'Test run results and trends',
        type: 'detailed'
      },
      {
        id: 'bug',
        name: 'Bug Report',
        description: 'Bug analysis and trends',
        type: 'detailed'
      },
      {
        id: 'activity',
        name: 'Activity Report',
        description: 'User and system activity',
        type: 'detailed'
      }
    ]
  }

  /**
   * Generate custom report
   * @param {Object} config - Report configuration
   * @returns {Promise<Object>} Custom report
   */
  async generateCustomReport(config) {
    return {
      config,
      report: {
        data: [],
        summary: {},
        generatedAt: new Date()
      }
    }
  }
}

export const reportService = new ReportService()
